import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import './App.css';

//Components
import Sidebar from './components/sidebar/Sidebar';
import CampSelectDialog from './components/sidebar/CampSelectDialog';
import InvitePage from './pages/invite/InvitePage';
import { Camp, ClubPage, Judge, EventTypePage, UserPage, LoginPage, EventPage, RubricPage, SettingsPage } from './pages';
import ClubImportWizard from './pages/club/import-wizard';
import ReportsIndex from './pages/reports';
import RubricsGradesDetailsPage from './pages/reports/rubrics-grades-details';
import NotasGeneralesPage from './pages/reports/notas-generales';
import GeneralGradesCertificatesPage from './pages/reports/general-grades-certificates';
import GradesByCategoriesPage from './pages/reports/grades-by-categories';
import { GradesConfirmationByJudges } from './pages/reports/grades-confirmation-by-judges';
import { EventInspectionsPage } from './pages/reports';
import { GradesByEvents } from './pages/reports/grades-by-events';
import RankingReport from './pages/reports/RankingReport';
import DeleteUser from './pages/DeleteUser';
import PendingGradesReport from './pages/reports/pending-grades';
import JudgePerformanceReport from './pages/reports/JudgePerformanceReport';
import PublicJudgePerformancePage from './pages/reports/PublicJudgePerformancePage';
import EventPerformanceDashboard from './pages/reports/EventPerformanceDashboard';
import DirectorLoginPage from './pages/director/login-page';
import DirectorChangePassword from './pages/director/change-password';
import DirectorResetPassword from './pages/director/reset-password';
import DirectorDashboard from './pages/director/dashboard';
import DirectorEventDetail from './pages/director/event-detail';
import DirectorSidebar from './components/director/DirectorSidebar';
import PrivateDirectorRoute from './components/director/PrivateDirectorRoute';
import ClubInvitePage from './pages/club-invite/ClubInvitePage';
import DirectorsPage from './pages/directors/directors-page';
import DirectorsImportWizard from './pages/directors/import-wizard';
import ObservabilityPage from './pages/observability';
import RequirePermission from './components/RequirePermission';

//Lib & Hook
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAuthContext } from './context/auth-context';
import { APP_BAR_COLOR } from './constants/env';
import { useNavigate } from 'react-router-dom';
import { ViewAsBanner } from './components/shared';

function App() {
  const { loggedIn, user, activeCamp } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { pathname } = location;

  useEffect(() => {
    // Rutas públicas — no redirigir aunque no haya sesión.
    // `/auth/login` va en la lista: sin ella el efecto navegaba al login estando
    // ya en él y, como la dependencia era el objeto `location` (nuevo en cada
    // navegación), se re-disparaba en bucle infinito.
    if (
      pathname === '/delete-account' ||
      pathname === '/auth/login' ||
      pathname.startsWith('/invite/') ||
      pathname.startsWith('/director/') ||
      pathname.startsWith('/club-invite/') ||
      pathname.startsWith('/public/')
    ) {
      return;
    }

    if (loggedIn === false) {
      // `replace` evita acumular entradas de historial en cada redirección.
      navigate('/auth/login', { replace: true });
    }
    // Dependemos de `pathname` (string) y no de `location` (objeto nuevo en cada
    // navegación) para que el efecto no se reactive al navegar a la misma ruta.
  }, [loggedIn, navigate, pathname]);

  // Rutas que usan el layout del director (con DirectorSidebar)
  const isDirectorAppRoute =
    location.pathname.startsWith('/director/dashboard') ||
    location.pathname.startsWith('/director/evento');

  // Rutas públicas que no necesitan ningún sidebar
  const isPublicRoute =
    location.pathname === '/delete-account' ||
    location.pathname === '/auth/login' ||
    location.pathname.startsWith('/invite/') ||
    location.pathname.startsWith('/director/login') ||
    location.pathname.startsWith('/director/change-password') ||
    location.pathname.startsWith('/director/reset-password') ||
    location.pathname.startsWith('/club-invite/') ||
    location.pathname.startsWith('/public/');

  const showSidebar        = !isPublicRoute && !isDirectorAppRoute && loggedIn === true && user;
  const showDirectorLayout = isDirectorAppRoute;

  const routes = (
    <Routes>
      {/* Rutas públicas - NO requieren autenticación */}
      <Route path="/delete-account"            element={<DeleteUser />} />
      <Route path='/auth/login'                element={<LoginPage />} />
      <Route path='/invite/:campId'            element={<InvitePage />} />
      {/* Director — portal independiente, sin sidebar del admin */}
      <Route path='/director/login'            element={<DirectorLoginPage />} />
      <Route path='/director/change-password'  element={<DirectorChangePassword />} />
      <Route path='/director/reset-password'   element={<DirectorResetPassword />} />
      {/* Director — rutas protegidas con layout propio */}
      <Route path='/director/dashboard'        element={<PrivateDirectorRoute><DirectorDashboard /></PrivateDirectorRoute>} />
      <Route path='/director/evento/:id'       element={<PrivateDirectorRoute><DirectorEventDetail /></PrivateDirectorRoute>} />
      <Route path='/club-invite/:campId'                    element={<ClubInvitePage />} />
      <Route path='/public/judge-performance/:campId'      element={<PublicJudgePerformancePage />} />

      {/* Rutas protegidas */}
      {['/', 'user'].map(path => (
        <Route key={path} path={path} element={<RequirePermission perm='users.view'><UserPage /></RequirePermission>} />
      ))}
      <Route path='rubric' element={<RequirePermission perm='rubrics.manage'><RubricPage /></RequirePermission>} />
      <Route path='campamento' element={<RequirePermission perm='camps.manage'><Camp /></RequirePermission>} />
      <Route path='judge' element={<RequirePermission perm='rubrics.manage'><Judge /></RequirePermission>} />

      <Route path='clubes' element={<RequirePermission perm='clubs.view'><ClubPage /></RequirePermission>} />
      <Route path='clubes/import' element={<RequirePermission perm='clubs.manage'><ClubImportWizard /></RequirePermission>} />
      <Route path='directors' element={<RequirePermission perm='directors.view'><DirectorsPage /></RequirePermission>} />
      <Route path='directors/import' element={<RequirePermission perm='directors.manage'><DirectorsImportWizard /></RequirePermission>} />

      <Route path='events' element={<RequirePermission perm='events.manage'><EventPage /></RequirePermission>} />
      <Route path='reports' element={<RequirePermission perm='reports.view'><ReportsIndex /></RequirePermission>} />
      <Route path='reports/rubrics-grades-details' element={<RequirePermission perm='reports.view'><RubricsGradesDetailsPage /></RequirePermission>} />
      <Route path='reports/notas-generales' element={<RequirePermission perm='reports.view'><NotasGeneralesPage /></RequirePermission>} />
      <Route path='reports/general-grades-certificates' element={<RequirePermission perm='reports.view'><GeneralGradesCertificatesPage /></RequirePermission>} />
      <Route path='reports/grades-by-categories' element={<RequirePermission perm='reports.view'><GradesByCategoriesPage /></RequirePermission>} />
      <Route path='reports/event-inspections' element={<RequirePermission perm='reports.view'><EventInspectionsPage /></RequirePermission>} />
      <Route path="/reports/grades-confirmation-by-judges" element={<RequirePermission perm='reports.view'><GradesConfirmationByJudges /></RequirePermission>} />
      <Route path="/reports/grades-by-events" element={<RequirePermission perm='reports.view'><GradesByEvents /></RequirePermission>} />
      <Route path="/reports/ranking" element={<RequirePermission perm='reports.view'><RankingReport /></RequirePermission>} />
      <Route path="/reports/pending-grades" element={<RequirePermission perm='reports.view'><PendingGradesReport /></RequirePermission>} />
      <Route path="/reports/judge-performance" element={<RequirePermission perm='reports.view-all-zones'><JudgePerformanceReport /></RequirePermission>} />
      <Route path="/reports/event-performance" element={<RequirePermission perm='reports.view-all-zones'><EventPerformanceDashboard /></RequirePermission>} />
      <Route path='settings' element={<RequirePermission perm='affiliations.manage'><SettingsPage /></RequirePermission>} />
      <Route path='watchtower' element={<RequirePermission perm='watchtower.view'><ObservabilityPage /></RequirePermission>} />

      {/* Ruta standalone para eliminar usuario */}
      <Route path="/delete-account" element={<DeleteUser />} />
    </Routes>
  );

  if (!showSidebar && !showDirectorLayout) {
    return <div className="App">{routes}</div>;
  }

  const mobileAppBar = (title) => (
    <AppBar
      position="fixed"
      sx={{
        display: { xs: 'flex', md: 'none' },
        backgroundColor: APP_BAR_COLOR,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={() => setMobileOpen(true)}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          {title}
        </Typography>
      </Toolbar>
    </AppBar>
  );

  if (showDirectorLayout) {
    return (
      <Box className="App" sx={{ display: 'flex', minHeight: '100vh' }}>
        {mobileAppBar('Portal Director')}
        <DirectorSidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: '#f4f1e9',
            pt: { xs: 'calc(56px + 24px)', md: 4 },
            pb: { xs: 3, md: 4 },
            px: { xs: 2, md: 3 },
            overflowX: 'hidden',
          }}
        >
          {routes}
        </Box>
      </Box>
    );
  }

  return (
    <Box className="App" sx={{ display: 'flex', minHeight: '100vh' }}>
      {mobileAppBar('Camp Manager')}
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          backgroundColor: '#f4f1e9',
          pt: { xs: 'calc(56px + 24px)', md: 4 },
          pb: { xs: 3, md: 4 },
          px: { xs: 2, md: 3 },
          overflowX: 'hidden',
        }}
      >
        {/* El aviso de vista previa va DENTRO del contenido, no flotando sobre él:
            así hereda el hueco del AppBar móvil y no puede tapar los botones de
            acción de ninguna página. La entrada al control vive en el Sidebar. */}
        <ViewAsBanner />
        {routes}
      </Box>

      {/* Forzar selección de camp si no hay uno activo */}
      <CampSelectDialog
        open={!activeCamp}
        onClose={() => {}}
        disableClose
      />
    </Box>
  );
}

export default App;
